import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ScopeLinkSelect } from '@/components/ScopeLinkSelect';
import {
  Plus, Clock, ListTodo, File, Pencil, X, Trash2, AlertCircle, ArrowRight,
  MessageSquareText, Home, ClipboardCheck, Building2, ChevronDown, ChevronRight, Link2, ClipboardList, FireExtinguisher, Ticket, Mail, Send, AlertTriangle, Paperclip, Upload, Layers, RefreshCw, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Checkbox } from '@/components/ui/checkbox';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import axios from 'axios';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import StatusDot, { statusColor, pillStyle } from '@/components/StatusDot';
import { CotcoChecklist } from '@/components/CotcoChecklist';
import { DobFilings } from '@/components/DobFilings';
import DotPermitsTab from '@/pages/DotPermitsTab';
import DotBatchTrackers from '@/components/DotBatchTrackers';
import FdnyPermits from '@/components/FdnyPermits';
import ViolationsTab from '@/pages/ViolationsTab';
import { InspectionsTab } from '@/components/InspectionsTab';
import { DocumentsSection } from '@/components/documents/DocumentsSection';

const PROJECT_STATUSES = ['Draft', 'In Progress', 'Filing', 'Review', 'Approved', 'Completed', 'Archived'];
const SCOPE_STATUS_OPTIONS = ['Not Started', 'To Be Filed', 'In Progress', 'Approved', 'Completed', 'N/A'];
const TODO_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed', 'On Hold'];

const LABEL_STYLE = { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' };
const MONO_VALUE_STYLE = { fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#010A21' };

const EMPTY_EDIT = {
  name: '', client_id: '', property_id: '', project_manager_id: '',
  primary_agency: '', contract_type: '', start_date: '', end_date: '',
  quote_number: '', address: '', description: '',
};

const EMPTY_TODO = { title: '', description: '', assigned_to: '', priority: 'Medium', due_date: '', linked_checklist_item_id: '' };

export default function ProjectDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const confirm = useConfirm();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);
  const [inline, setInline] = useState({});

  const [statusText, setStatusText] = useState('');
  const [actionItems, setActionItems] = useState('');
  const [todoFormOpenFor, setTodoFormOpenFor] = useState(null); // item_id | '__other' | null
  const [todoForm, setTodoForm] = useState(EMPTY_TODO);
  const [editTodo, setEditTodo] = useState(null);
  const [editTodoForm, setEditTodoForm] = useState({});
  const [scopeProgress, setScopeProgress] = useState(null);
  const [scopeChecklist, setScopeChecklist] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [linkFormFor, setLinkFormFor] = useState(null); // item_id whose "link filing" form is open
  const [linkForm, setLinkForm] = useState({ type: 'dob', number: '' });
  const [linkBusy, setLinkBusy] = useState(false);
  const [projectReminders, setProjectReminders] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [meetingNoteForm, setMeetingNoteForm] = useState({ date: '', attendees: '', notes: '' });
  const [meetingNoteFormOpen, setMeetingNoteFormOpen] = useState(false);
  const [editMeetingNote, setEditMeetingNote] = useState(null);
  const [editMeetingNoteForm, setEditMeetingNoteForm] = useState({});
  const [searchParams] = useSearchParams();
  const validTabs = ['todos', 'meeting-notes', 'docs', 'cotco', 'dot-permits', 'dot-batch', 'dob-filings', 'fdny-permits', 'violations', 'inspections'];
  const [activeTab, setActiveTab] = useState(validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'todos');
  const [addStatusOpen, setAddStatusOpen] = useState(false);
  const [statusLogOpen, setStatusLogOpen] = useState(false);
  const [todoFromReminder, setTodoFromReminder] = useState(null);
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
  const { user } = useAuth();
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [projectEmails, setProjectEmails] = useState([]);
  // Attachments (compose)
  const [projectDocs, setProjectDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const fileInputRef = useRef(null);
  // Recipients (To + CC) sourced from the project's client contacts, each a {email, label}
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [toRecipients, setToRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [toCustom, setToCustom] = useState('');
  const [ccCustom, setCcCustom] = useState('');

  const loadEmails = useCallback(() => {
    api.get(`/projects/${id}/emails`).then(r => setProjectEmails(Array.isArray(r.data) ? r.data : [])).catch(() => setProjectEmails([]));
  }, [id]);

  // Build the recipient list from the project's client + its contacts, each tagged with its source.
  const buildRecipientOptions = (client, contacts) => {
    const opts = [];
    const seen = new Set();
    const add = (source, name, email) => {
      const e = (email || '').trim();
      if (!e || seen.has(e.toLowerCase())) return;
      seen.add(e.toLowerCase());
      opts.push({ key: `${source}-${e}`.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase(), source, name: name || '', email: e });
    };
    if (client) {
      add('Primary Contact', client.primary_contact_detail?.name, client.primary_contact_detail?.email);
      add('Billable Contact', client.billable_contact_detail?.name, client.billable_contact_detail?.email);
      add('Client', client.name, client.email);
      (Array.isArray(contacts) ? contacts : [])
        .filter(ct => Array.isArray(ct.linked_clients) && ct.linked_clients.includes(client.id))
        .forEach(ct => add('Contact', ct.name, ct.email));
    }
    return opts;
  };

  // Label a contact option for display, e.g. "Primary Contact: Jane Doe".
  const optLabel = (o) => `${o.source}${o.name ? `: ${o.name}` : ''}`;

  const addRecipient = (list, setList, email, label) => {
    const e = (email || '').trim();
    if (!e) return;
    if (list.some(r => r.email.toLowerCase() === e.toLowerCase())) return;
    setList([...list, { email: e, label: label || 'Custom address' }]);
  };
  const removeRecipient = (list, setList, email) => setList(list.filter(r => r.email !== email));

  const openCompose = () => {
    setEmailForm({ subject: '', body: '' });
    setSelectedDocIds([]);
    setNewFiles([]);
    setRecipientOptions([]);
    setToRecipients([]);
    setCcRecipients([]);
    setToCustom('');
    setCcCustom('');
    api.get(`/documents?record_type=Project&record_id=${id}`)
      .then(r => setProjectDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjectDocs([]));
    if (selected?.client_id) {
      Promise.all([
        api.get(`/clients/${selected.client_id}`),
        api.get('/contacts'),
      ]).then(([cr, ctr]) => {
        const opts = buildRecipientOptions(cr.data, ctr.data);
        setRecipientOptions(opts);
        if (opts.length) {
          // Prefill To with the first available contact (usually the Primary Contact).
          setToRecipients([{ email: opts[0].email, label: optLabel(opts[0]) }]);
        }
      }).catch(() => {});
    }
    setEmailComposeOpen(true);
  };

  const toggleDocSelected = (docId) => {
    setSelectedDocIds(prev => prev.includes(docId) ? prev.filter(x => x !== docId) : [...prev, docId]);
  };

  const onPickNewFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setNewFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const sendProjectEmail = async () => {
    const toStr = toRecipients.map(r => r.email).join(', ');
    const ccStr = ccRecipients.map(r => r.email).join(', ');
    if (!toRecipients.length || !emailForm.subject.trim() || !emailForm.body.trim()) {
      toast.error('At least one To recipient, a subject, and a message are all required'); return;
    }
    setSendingEmail(true);
    try {
      let r;
      if (newFiles.length > 0) {
        // Multipart: bare axios so the browser sets the multipart boundary (the shared
        // `api` instance forces Content-Type: application/json which would clobber it).
        const fd = new FormData();
        fd.append('to', toStr);
        fd.append('cc', ccStr);
        fd.append('subject', emailForm.subject);
        fd.append('body', emailForm.body);
        fd.append('attachment_document_ids', JSON.stringify(selectedDocIds));
        newFiles.forEach(f => fd.append('files', f));
        const token = localStorage.getItem('token');
        r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/emails`, fd, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } else {
        r = await api.post(`/projects/${id}/emails`, { to: toStr, cc: ccStr, subject: emailForm.subject, body: emailForm.body, attachment_document_ids: selectedDocIds });
      }
      if (r.data.delivered) toast.success('Email sent and logged to this project');
      else toast.success('Email logged to this project (delivery pending SMTP setup)');
      setEmailComposeOpen(false);
      setEmailForm({ subject: '', body: '' });
      setToRecipients([]);
      setCcRecipients([]);
      setSelectedDocIds([]);
      setNewFiles([]);
      loadEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error sending email'); }
    setSendingEmail(false);
  };

  const loadProject = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${id}`);
      setSelected(r.data);
      setMeetingNotes(r.data.meeting_notes || []);
      api.get(`/action-item-reminders?project_id=${id}`).then(res => setProjectReminders(Array.isArray(res.data) ? res.data : [])).catch(() => setProjectReminders([]));
    } catch (e) {
      setNotFound(true);
    }
    setLoading(false);
  }, [id]);

  const loadScope = useCallback(async () => {
    try {
      const [sp, cl] = await Promise.all([
        api.get(`/projects/${id}/scope-progress`),
        api.get(`/compliance?project_id=${id}`),
      ]);
      setScopeProgress(sp.data || null);
      const list = Array.isArray(cl.data) ? cl.data : [];
      setScopeChecklist(list.find(c => c.template_type === 'Proposal Scope') || null);
    } catch {
      setScopeProgress(null);
      setScopeChecklist(null);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
    loadScope();
    loadEmails();
    api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [loadProject, loadScope, loadEmails]);

  const refreshSelected = useCallback(async () => {
    const r = await api.get(`/projects/${id}`);
    setSelected(r.data);
    setMeetingNotes(r.data.meeting_notes || []);
  }, [id]);

  // Sync inline-editable strip fields when the project changes.
  useEffect(() => {
    setInline({
      primary_agency: selected?.primary_agency || '',
      contract_type: selected?.contract_type || '',
      start_date: selected?.start_date || '',
      end_date: selected?.end_date || '',
      project_manager_id: selected?.project_manager_id || '',
      quote_number: selected?.quote_number || '',
    });
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave a single project field (used by the inline-editable info strip).
  const saveField = async (field, value) => {
    const v = value === '' ? null : value;
    try {
      await api.put(`/projects/${id}`, { [field]: v });
      setSelected(prev => (prev ? { ...prev, [field]: v } : prev));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const pmName = (uid) => (Array.isArray(users) ? users : []).find(u => u.id === uid)?.name || ''; // eslint-disable-line no-unused-vars

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/projects/${id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  const openEdit = () => {
    setEditForm({
      name: selected.name || '', client_id: selected.client_id || '', property_id: selected.property_id || '',
      project_manager_id: selected.project_manager_id || '', primary_agency: selected.primary_agency || '',
      contract_type: selected.contract_type || '', start_date: selected.start_date || '',
      end_date: selected.end_date || '', quote_number: selected.quote_number || '',
      address: selected.address || '', description: selected.description || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error('Project name is required');
    setSavingEdit(true);
    try {
      await api.put(`/projects/${id}`, {
        ...editForm,
        client_id: editForm.client_id || null,
        property_id: editForm.property_id || null,
        project_manager_id: editForm.project_manager_id || null,
      });
      toast.success('Project updated');
      setEditOpen(false);
      refreshSelected();
      setInline({
        primary_agency: editForm.primary_agency || '',
        contract_type: editForm.contract_type || '',
        start_date: editForm.start_date || '',
        end_date: editForm.end_date || '',
        project_manager_id: editForm.project_manager_id || '',
        quote_number: editForm.quote_number || '',
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating project'); }
    setSavingEdit(false);
  };

  const deleteProject = async () => {
    const ok = await confirm({ title: 'Delete project', description: 'Delete this project? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting project'); }
  };

  const addStatusEntry = async () => {
    if (!statusText.trim()) return toast.error('Status text required');
    await api.post(`/projects/${id}/status`, { status_text: statusText, action_items: actionItems });
    toast.success('Status update added');
    setStatusText('');
    setActionItems('');
    setAddStatusOpen(false);
    refreshSelected();
  };

  // ─── Auto status bump (Section D) ───
  const maybeAutoBump = async () => {
    try {
      const [pr, sp] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/scope-progress`),
      ]);
      const proj = pr.data || {};
      const items = (sp.data && Array.isArray(sp.data.items)) ? sp.data.items : [];
      const tasks = Array.isArray(proj.tasks) ? proj.tasks : [];
      const itemIds = new Set(items.map(i => i.item_id));
      const projectTodos = tasks.filter(t => !t.linked_checklist_item_id || !itemIds.has(t.linked_checklist_item_id));
      const cur = proj.status;
      if (cur === 'Not Started') {
        const progressed = items.some(i => i.is_complete || (i.status && i.status !== 'Not Started') || (i.completed_task_count || 0) > 0);
        if (progressed) {
          await api.put(`/projects/${id}`, { status: 'In Progress' });
          toast.success('Project status auto-updated to In Progress');
          refreshSelected();
          return;
        }
      }
      if (cur !== 'Completed' && items.length > 0) {
        const allScope = items.every(i => i.is_complete);
        const allTodos = projectTodos.every(t => t.status === 'Completed');
        if (allScope && allTodos) {
          try {
            await api.put(`/projects/${id}`, { status: 'Completed' });
            toast.success('Project status auto-updated to Completed');
            refreshSelected();
          } catch { /* backend may block completion; leave as-is */ }
        }
      }
    } catch { /* ignore */ }
  };

  const updateTodoStatus = async (tid, status) => {
    try {
      await api.put(`/tasks/${tid}`, { status });
      toast.success('Status updated');
      await refreshSelected();
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update status'); }
  };

  // ─── To-Dos / Scope ───

  const openCreateTodo = (linkItemId) => {
    setEditTodo(null);
    setTodoFromReminder(null);
    setTodoForm({ ...EMPTY_TODO, linked_checklist_item_id: linkItemId || '' });
    setTodoFormOpenFor(linkItemId || '__other');
    if (linkItemId) setExpandedItems(prev => ({ ...prev, [linkItemId]: true }));
  };

  const createTodo = async () => {
    if (!todoForm.title) return toast.error('Title required');
    try {
      await api.post('/tasks', {
        ...todoForm,
        project_id: id,
        linked_checklist_item_id: todoForm.linked_checklist_item_id || null,
      });
      toast.success('To-Do created');
      if (todoFromReminder) { await dismissProjectReminder(todoFromReminder); setTodoFromReminder(null); }
      setTodoForm(EMPTY_TODO);
      setTodoFormOpenFor(null);
      await refreshSelected();
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating To-Do'); }
  };

  const openEditTodo = (t) => {
    setEditTodo(t);
    setEditTodoForm({
      title: t.title || '', description: t.description || '',
      assigned_to: t.assigned_to || '', priority: t.priority || 'Medium', status: t.status || 'Not Started', due_date: t.due_date || '',
      linked_checklist_item_id: t.linked_checklist_item_id || '',
    });
    setTodoFormOpenFor(null);
  };

  const saveEditTodo = async () => {
    if (!editTodoForm.title) return toast.error('Title required');
    try {
      await api.put(`/tasks/${editTodo.id}`, {
        ...editTodoForm,
        linked_checklist_item_id: editTodoForm.linked_checklist_item_id || null,
      });
      toast.success('To-Do updated');
      setEditTodo(null);
      await refreshSelected();
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating To-Do'); }
  };

  const deleteTodo = async (t) => {
    const ok = await confirm({ title: 'Delete to-do', description: `Delete "${t.title}"? This cannot be undone.`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success('To-Do deleted');
      if (editTodo?.id === t.id) setEditTodo(null);
      await refreshSelected();
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting To-Do'); }
  };

  const updateScopeItemStatus = async (itemId, status) => {
    if (!scopeChecklist?.id) return;
    try {
      await api.put(`/compliance/${scopeChecklist.id}/items/${itemId}`, { status });
      toast.success('Status updated');
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error('Could not update status'); }
  };

  // ─── Filing / permit linkage (Sections B & C) ───
  const FILING_TYPE_LABELS = { dob: 'DOB', dot: 'DOT', fdny: 'FDNY' };
  const isSignedOff = (s) => { const l = (s || '').toLowerCase(); return ['complet', 'signed-off', 'signed off', 'sign-off', 'closed'].some(k => l.includes(k)); };
  // Per-filing pill color: a DOB/DOT with no Open Data match reads muted grey; a signed-off
  // DOB reads green; disapproved/denied/rejected read scarlet; else map the raw status value.
  const filingPillColor = (f) => {
    const live = f.live_status;
    if ((f.type === 'dob' || f.type === 'dot') && !live) return '#71717A';
    if (f.type === 'dob' && isSignedOff(live)) return '#16A34A';
    const l = (live || '').toLowerCase();
    if (l.includes('disapprov') || l.includes('denied') || l.includes('rejected')) return '#E5441A';
    return statusColor(live || '');
  };
  const itemFilings = (item) => (Array.isArray(item.linked_filings) ? item.linked_filings : []);
  // A linked DOB/DOT whose live status is null found no matching NYC Open Data record.
  const isNoOpenDataMatch = (f) => f && (f.type === 'dob' || f.type === 'dot') && !f.live_status;

  const openLinkForm = (itemId) => {
    setLinkForm({ type: 'dob', number: '' });
    setLinkFormFor(itemId);
    setExpandedItems(prev => ({ ...prev, [itemId]: true }));
  };

  const submitLinkFiling = async (item) => {
    if (!scopeChecklist?.id) return;
    const number = (linkForm.number || '').trim();
    if (!number) return toast.error('Enter a filing/permit number');
    setLinkBusy(true);
    try {
      const r = await api.post(`/compliance/${scopeChecklist.id}/items/${item.item_id}/link-filing`, { type: linkForm.type, number });
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Filing linked');
      setLinkFormFor(null);
      setLinkForm({ type: 'dob', number: '' });
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link filing'); }
    finally { setLinkBusy(false); }
  };

  const refreshFiling = async (item, recordId) => {
    if (!scopeChecklist?.id) return;
    try {
      const r = await api.post(`/compliance/${scopeChecklist.id}/items/${item.item_id}/refresh-filing`, recordId ? { record_id: recordId } : {});
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Refreshed from Open Data');
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error('Could not refresh filing'); }
  };

  const unlinkFiling = async (item, recordId) => {
    if (!scopeChecklist?.id) return;
    const ok = await confirm({ title: 'Unlink filing', description: 'Remove this linked filing/permit from the scope item? The underlying record is kept.', confirmLabel: 'Unlink' });
    if (!ok) return;
    try {
      await api.delete(`/compliance/${scopeChecklist.id}/items/${item.item_id}/link-filing`, { params: recordId ? { record_id: recordId } : {} });
      toast.success('Filing unlinked');
      await loadScope();
      maybeAutoBump();
    } catch (e) { toast.error('Could not unlink filing'); }
  };

  const toggleItem = (itemId) => setExpandedItems(prev => ({ ...prev, [itemId]: !(prev[itemId] === undefined ? false : prev[itemId]) }));

  const createMeetingNote = async () => {
    if (!meetingNoteForm.notes.trim()) return toast.error('Notes content is required');
    try {
      const res = await api.post(`/projects/${id}/meeting-notes`, {
        date: meetingNoteForm.date || new Date().toISOString().slice(0, 10),
        attendees: meetingNoteForm.attendees, notes: meetingNoteForm.notes,
      });
      toast.success('Meeting note added');
      setMeetingNotes(prev => [res.data, ...prev]);
      setMeetingNoteForm({ date: '', attendees: '', notes: '' });
      setMeetingNoteFormOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating note'); }
  };

  const openEditMeetingNote = (n) => {
    setEditMeetingNote(n);
    setEditMeetingNoteForm({ date: n.date || '', attendees: n.attendees || '', notes: n.notes || '' });
    setMeetingNoteFormOpen(false);
  };

  const saveEditMeetingNote = async () => {
    if (!editMeetingNoteForm.notes.trim()) return toast.error('Notes content is required');
    try {
      const res = await api.put(`/projects/${id}/meeting-notes/${editMeetingNote.id}`, editMeetingNoteForm);
      toast.success('Meeting note updated');
      setMeetingNotes(prev => prev.map(n => n.id === editMeetingNote.id ? res.data : n));
      setEditMeetingNote(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating note'); }
  };

  const deleteMeetingNote = async (noteId) => {
    try {
      await api.delete(`/projects/${id}/meeting-notes/${noteId}`);
      toast.success('Meeting note deleted');
      setMeetingNotes(prev => prev.filter(n => n.id !== noteId));
      if (editMeetingNote?.id === noteId) setEditMeetingNote(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting note'); }
  };

  const dismissProjectReminder = async (reminderId) => {
    await api.put(`/action-item-reminders/${reminderId}/dismiss`);
    setProjectReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  const clearAllProjectReminders = async () => {
    const ok = await confirm({
      title: 'Clear all suggestions',
      description: `Clear all ${projectReminders.length} pending To-Do suggestion(s) for this project? This only removes the suggestions - any To-Do you've already created stays.`,
      confirmLabel: 'Clear All',
    });
    if (!ok) return;
    await api.put(`/action-item-reminders/dismiss-all?project_id=${id}`);
    setProjectReminders([]);
    toast.success('Suggestions cleared');
  };

  const createTodoFromProjectReminder = (reminder) => {
    setEditTodo(null);
    setTodoFromReminder(reminder.id);
    setTodoForm({
      ...EMPTY_TODO,
      title: reminder.action_items.length > 80 ? reminder.action_items.substring(0, 80) + '...' : reminder.action_items,
      description: `From status update: ${reminder.action_items}`,
    });
    setTodoFormOpenFor('__other');
  };

  const clientOptions = (Array.isArray(clients) ? clients : []).map(c => ({ value: c.id, label: c.name }));
  const propertyOptions = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address} (${p.borough})` }));

  if (loading) return <div className="animate-pulse text-muted-foreground py-8">Loading project...</div>;
  if (notFound || !selected) return (
    <div className="space-y-4 py-8" data-testid="project-not-found">
      <button onClick={() => navigate('/projects')} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="project-breadcrumb-back">Projects</button>
      <p className="text-sm text-muted-foreground">Project not found.</p>
    </div>
  );

  const scopeItems = (scopeProgress && Array.isArray(scopeProgress.items)) ? scopeProgress.items : [];
  const allTasks = Array.isArray(selected.tasks) ? selected.tasks : [];
  const scopeItemIds = new Set(scopeItems.map(i => i.item_id));
  const tasksForItem = (itemId) => allTasks.filter(t => t.linked_checklist_item_id === itemId);
  // "Other" = tasks with no link OR a link that no longer matches a scope item
  const otherTodos = allTasks.filter(t => !t.linked_checklist_item_id || !scopeItemIds.has(t.linked_checklist_item_id));
  const otherDone = otherTodos.filter(t => t.status === 'Completed').length;
  const scopeItemName = (itemId) => (scopeItems.find(i => i.item_id === itemId) || {}).activity || '';
  const isItemOpen = (item) => (expandedItems[item.item_id] === undefined ? item.linked_task_count > 0 : expandedItems[item.item_id]);

  const scopeLinkField = (form, setForm, testid) => (
    <ScopeLinkSelect
      items={scopeItems}
      value={form.linked_checklist_item_id}
      onChange={v => setForm({ ...form, linked_checklist_item_id: v })}
      testid={testid}
    />
  );

  const createFormJsx = () => (
    <Card className="border-border border-dashed bg-transparent shadow-none" data-testid="project-todo-create-form">
      <CardContent className="p-3 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">New To-Do</p>
        <Input placeholder="Title *" value={todoForm.title} onChange={e => setTodoForm({ ...todoForm, title: e.target.value })} className="bg-background border-border" data-testid="project-todo-title" />
        <ExpandableTextarea placeholder="Description" value={todoForm.description} onChange={e => setTodoForm({ ...todoForm, description: e.target.value })} className="bg-background border-border" rows={2} expandedRows={6} />
        {scopeLinkField(todoForm, setTodoForm, 'project-todo-scope-link')}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Assign To</Label>
            <Select value={todoForm.assigned_to} onValueChange={v => setTodoForm({ ...todoForm, assigned_to: v })}>
              <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client' && u.role !== 'Administrator').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Priority</Label>
            <Select value={todoForm.priority} onValueChange={v => setTodoForm({ ...todoForm, priority: v })}>
              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Input type="date" placeholder="Due date" value={todoForm.due_date} onChange={e => setTodoForm({ ...todoForm, due_date: e.target.value })} className="bg-background border-border" />
        <div className="flex gap-2">
          <Button size="sm" onClick={createTodo} className="bg-primary text-primary-foreground" data-testid="project-todo-submit"><Plus className="h-3 w-3 mr-1" /> Create To-Do</Button>
          <Button size="sm" variant="outline" onClick={() => setTodoFormOpenFor(null)}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );

  const editFormJsx = () => (
    <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none" data-testid="project-todo-edit-form">
      <CardContent className="p-3 space-y-2">
        <p className="text-xs font-semibold text-primary">Edit To-Do</p>
        <Input placeholder="Title *" value={editTodoForm.title} onChange={e => setEditTodoForm({ ...editTodoForm, title: e.target.value })} className="bg-background border-border" data-testid="edit-todo-title" />
        <ExpandableTextarea placeholder="Description" value={editTodoForm.description} onChange={e => setEditTodoForm({ ...editTodoForm, description: e.target.value })} className="bg-background border-border" rows={2} expandedRows={6} />
        {scopeLinkField(editTodoForm, setEditTodoForm, 'edit-todo-scope-link')}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Assign To</Label>
            <Select value={editTodoForm.assigned_to} onValueChange={v => setEditTodoForm({ ...editTodoForm, assigned_to: v })}>
              <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client' && u.role !== 'Administrator').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Priority</Label>
            <Select value={editTodoForm.priority} onValueChange={v => setEditTodoForm({ ...editTodoForm, priority: v })}>
              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={editTodoForm.status} onValueChange={v => setEditTodoForm({ ...editTodoForm, status: v })}>
              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{['Not Started', 'In Progress', 'Completed', 'On Hold'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Input type="date" value={editTodoForm.due_date || ''} onChange={e => setEditTodoForm({ ...editTodoForm, due_date: e.target.value })} className="bg-background border-border" />
        <div className="flex gap-2">
          <Button size="sm" onClick={saveEditTodo} className="bg-primary text-primary-foreground" data-testid="save-edit-todo-btn">Save Changes</Button>
          <Button size="sm" variant="outline" onClick={() => setEditTodo(null)}>Cancel</Button>
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border ml-auto" onClick={() => deleteTodo(editTodo)} data-testid="delete-todo-btn"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderTodoCard = (t) => (
    <div key={t.id}>
      <Card className="border-border bg-background shadow-none mb-1.5 cursor-pointer hover:border-primary/30 transition-colors" onClick={() => openEditTodo(t)} data-testid={`todo-card-${t.id}`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium" style={{ color: '#010A21' }}>{t.title}</p>
              {t.description && <p className="text-xs text-muted-foreground line-clamp-1">{t.description}</p>}
              {t.linked_checklist_item_id && scopeItemName(t.linked_checklist_item_id) && (
                <span className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#1B4A76' }} data-testid={`todo-scope-label-${t.id}`}>
                  <Link2 className="h-3 w-3" /> {scopeItemName(t.linked_checklist_item_id)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={`text-xs ${t.priority === 'High' ? 'text-destructive border-destructive/20' : t.priority === 'Medium' ? 'text-amber-400 border-amber-500/20' : ''}`}>{t.priority}</Badge>
              <div onClick={e => e.stopPropagation()}>
                <Select value={t.status} onValueChange={v => updateTodoStatus(t.id, v)}>
                  <SelectTrigger className="h-7 w-auto gap-1 border-border bg-background px-2 text-xs" data-testid={`todo-status-select-${t.id}`}>
                    <SelectValue><StatusDot status={t.status} /></SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {TODO_STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs"><StatusDot status={s} /></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Pencil className="h-3 w-3 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
      {editTodo?.id === t.id && editFormJsx()}
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="project-detail-page">
      {/* Breadcrumb */}
      <button onClick={() => navigate('/projects')} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="project-breadcrumb-back">
        ← Projects
      </button>

      {/* Header card */}
      <div className="rounded bg-card flex items-start justify-between gap-4 flex-wrap" style={{ border: '1px solid #D4DCE8', padding: '20px' }} data-testid="project-header-card">
        <div className="min-w-0">
          <p style={MONO_VALUE_STYLE} data-testid="project-header-number"><span style={{ color: '#031333' }}>{selected.project_number}</span></p>
          <h1 className="font-semibold mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '18px', color: '#010A21' }} data-testid="project-header-name">{selected.name}</h1>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
            {selected.client_name && selected.client_name !== 'N/A' && (
              <button onClick={() => navigate('/clients')} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="project-header-client-link">{selected.client_name}</button>
            )}
            {(selected.property_display_name || selected.property_address) && (
              <>
                <span style={{ color: '#031333' }}>·</span>
                <button onClick={() => navigate(`/properties/${selected.property_id}`)} className="hover:underline" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#1B4A76' }} data-testid="project-header-property-link">{selected.property_display_name || selected.property_address}</button>
              </>
            )}
          </div>
          {selected.address && <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#031333' }}>{selected.address}</p>}
          {selected.description && <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#031333' }}>{selected.description}</p>}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Select value={selected.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-44 h-9 bg-background border-border" data-testid="project-status-select">
              <span className="flex items-center"><StatusDot status={selected.status} /></span>
            </SelectTrigger>
            <SelectContent className="bg-card border-border">{PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-border" onClick={openEdit} data-testid="project-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
          </div>
        </div>
      </div>

      {/* Field details strip - inline editable, autosave on blur/change */}
      <div className="rounded bg-card flex flex-wrap gap-x-6 gap-y-3" style={{ border: '1px solid #D4DCE8', padding: '16px' }} data-testid="project-field-strip">
        <div className="flex flex-col gap-1 min-w-[160px]">
          <Label className="text-xs" style={{ color: '#031333' }}>Primary Agency</Label>
          <Input value={inline.primary_agency || ''} onChange={e => setInline({ ...inline, primary_agency: e.target.value })} onBlur={e => saveField('primary_agency', e.target.value)} placeholder="Not set" className="h-8 bg-background border-border text-sm" data-testid="project-inline-primary-agency" />
        </div>
        <div className="flex flex-col gap-1 min-w-[160px]">
          <Label className="text-xs" style={{ color: '#031333' }}>Contract Type</Label>
          <Input value={inline.contract_type || ''} onChange={e => setInline({ ...inline, contract_type: e.target.value })} onBlur={e => saveField('contract_type', e.target.value)} placeholder="Not set" className="h-8 bg-background border-border text-sm" data-testid="project-inline-contract-type" />
        </div>
        <div className="flex flex-col gap-1 min-w-[150px]">
          <Label className="text-xs" style={{ color: '#031333' }}>Start Date</Label>
          <Input type="date" value={inline.start_date || ''} onChange={e => { setInline({ ...inline, start_date: e.target.value }); saveField('start_date', e.target.value); }} className="h-8 bg-background border-border text-sm" data-testid="project-inline-start-date" />
        </div>
        <div className="flex flex-col gap-1 min-w-[150px]">
          <Label className="text-xs" style={{ color: '#031333' }}>End Date</Label>
          <Input type="date" value={inline.end_date || ''} onChange={e => { setInline({ ...inline, end_date: e.target.value }); saveField('end_date', e.target.value); }} className="h-8 bg-background border-border text-sm" data-testid="project-inline-end-date" />
        </div>
        <div className="flex flex-col gap-1 min-w-[180px]">
          <Label className="text-xs" style={{ color: '#031333' }}>Project Manager</Label>
          <Select value={inline.project_manager_id || '__none'} onValueChange={v => { const nv = v === '__none' ? '' : v; setInline({ ...inline, project_manager_id: nv }); saveField('project_manager_id', nv); }}>
            <SelectTrigger className="h-8 bg-background border-border text-sm" data-testid="project-inline-pm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="__none">Unassigned</SelectItem>
              {users.filter(u => u.role !== 'Client' && u.role !== 'Administrator').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 min-w-[150px]">
          <Label className="text-xs" style={{ color: '#031333' }}>Quote No.</Label>
          <Input value={inline.quote_number || ''} onChange={e => setInline({ ...inline, quote_number: e.target.value })} onBlur={e => saveField('quote_number', e.target.value)} placeholder="Not set" className="h-8 bg-background border-border text-sm" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="project-inline-quote" />
        </div>
      </div>

      {/* Linked Property */}
      {selected.property_id && (
        <Card className="border-border bg-secondary/30 shadow-none" data-testid="linked-property-card">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 " style={LABEL_STYLE}><Home className="h-3 w-3" /> Linked Property</p>
                <p className="text-sm font-medium mt-0.5" style={{ color: '#010A21' }}>{selected.property_display_name || selected.property_address}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  {selected.property_address && <span className="text-xs text-muted-foreground">{selected.property_address}</span>}
                  {selected.property_bin && <span className="text-xs text-muted-foreground" style={{ fontFamily: "'DM Mono', monospace" }}>BIN {selected.property_bin}</span>}
                </div>
              </div>
              <button onClick={() => navigate(`/properties/${selected.property_id}`)} className="text-sm text-[#1B4A76] hover:underline shrink-0" data-testid="view-property-link">View Property →</button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-end justify-between gap-2 border-b border-border flex-wrap">
          <TabsList className="justify-start rounded-none border-0 bg-transparent p-0 h-auto flex-wrap" data-testid="project-detail-tabs">
            {[
              ['todos', 'Scope Tracker', ListTodo],
              ['meeting-notes', 'Meeting Notes', MessageSquareText],
              ['docs', 'Documents', File],
              ['cotco', 'CO/TCO', ClipboardCheck],
              ['dot-permits', 'DOT Permits', Ticket],
              ['dot-batch', 'DOT Batches', Layers],
              ['dob-filings', 'DOB Filings', Building2],
              ['fdny-permits', 'FDNY Permits', FireExtinguisher],
              ['violations', 'Violations', AlertTriangle],
              ['inspections', 'Inspections', ClipboardList],
            ].map(([val, label, Icon]) => (
              <TabsTrigger key={val} value={val} className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-xs data-[state=active]:border-[#E5441A] data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:shadow-none" data-testid={`tab-${val}`}>
                <Icon className="h-3 w-3 mr-1" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex items-center gap-2 pb-1.5 ml-auto shrink-0">
            <Button size="sm" variant="outline" className="border-border h-8" onClick={() => { loadEmails(); setEmailHistoryOpen(true); }} data-testid="open-email-history-btn"><Mail className="h-3.5 w-3.5 mr-1" /> Email History</Button>
            <Button size="sm" variant="outline" className="border-border h-8" onClick={openCompose} data-testid="open-send-email-btn"><Send className="h-3.5 w-3.5 mr-1" /> Send Email</Button>
            <Button size="sm" variant="outline" className="border-border h-8" onClick={() => setStatusLogOpen(true)} data-testid="open-status-log-btn"><Clock className="h-3.5 w-3.5 mr-1" /> Status Log</Button>
            <Button size="sm" className="bg-primary text-primary-foreground h-8" onClick={() => setAddStatusOpen(true)} data-testid="open-add-status-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Status Update</Button>
          </div>
        </div>

        {/* To-Dos / Scope Checklist */}
        <TabsContent value="todos" className="mt-4 space-y-3">
          {/* Scope progress summary */}
          {scopeProgress && scopeProgress.total_items > 0 && (
            <div className="rounded-md border border-border bg-secondary/30 px-3 py-2.5" data-testid="scope-summary-bar">
              <p className="text-sm font-medium" style={{ color: '#010A21' }} data-testid="scope-summary-text">
                Scope Checklist: {scopeProgress.percent}% complete ({scopeProgress.completed_items} of {scopeProgress.total_items} items)
              </p>
              <div className="w-full h-2 rounded-full bg-secondary mt-2 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${scopeProgress.percent}%`, backgroundColor: '#16A34A' }} />
              </div>
            </div>
          )}

          {projectReminders.length > 0 && (
            <div className="space-y-1.5" data-testid="project-reminders">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">To-Do suggestions ({projectReminders.length})</p>
                <Button size="sm" variant="ghost" className="h-6 text-xs text-muted-foreground hover:text-foreground gap-1 px-1.5" onClick={clearAllProjectReminders} data-testid="proj-reminders-clear-all"><X className="h-3 w-3" /> Clear All</Button>
              </div>
              {projectReminders.map(r => (
                <div key={r.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-amber-500/20 bg-amber-500/5" data-testid={`project-reminder-${r.id}`}>
                  <AlertCircle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-amber-400 font-medium">Action item from status update</p>
                    <p className="text-sm mt-0.5">{r.action_items}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.created_by_name && `${r.created_by_name} · `}{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-6 text-xs text-primary hover:text-primary/80 gap-0.5 px-1.5" onClick={() => createTodoFromProjectReminder(r)} data-testid={`proj-reminder-create-${r.id}`}><ArrowRight className="h-3 w-3" /> To-Do</Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" onClick={() => dismissProjectReminder(r.id)} data-testid={`proj-reminder-dismiss-${r.id}`}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Scope item sections */}
          {scopeItems.map(item => {
            const itemTasks = tasksForItem(item.item_id);
            const linkedCount = item.linked_task_count;
            const open = isItemOpen(item);
            const filings = itemFilings(item);
            const hasFilings = filings.length > 0;
            const dobFilings = filings.filter(f => f.type === 'dob');
            const hasDob = dobFilings.length > 0;
            const signedOffCount = dobFilings.filter(f => isSignedOff(f.live_status)).length;
            const showManual = hasFilings && !hasDob && linkedCount === 0;
            return (
              <div key={item.item_id} className="border border-border rounded-md bg-card" data-testid={`scope-item-${item.item_id}`}>
                <div className="flex items-center justify-between gap-3 px-3 py-2">
                  <button className="flex items-start gap-2 min-w-0 flex-1 text-left" onClick={() => toggleItem(item.item_id)} data-testid={`scope-item-toggle-${item.item_id}`}>
                    {open ? <ChevronDown className="h-4 w-4 shrink-0 mt-0.5" /> : <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />}
                    <span className="min-w-0">
                      <span className="text-sm font-medium block" style={{ color: '#010A21' }}>{item.activity}</span>
                      {linkedCount === 0 && !hasFilings && item.scope_detail && (
                        <span className="text-xs text-muted-foreground block mt-0.5 leading-snug" data-testid={`scope-item-detail-${item.item_id}`}>{item.scope_detail}</span>
                      )}
                    </span>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${item.percent}%`, backgroundColor: item.is_complete ? '#16A34A' : (linkedCount > 0 || hasFilings ? '#1B4A76' : '#D4DCE8') }} />
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`scope-item-progress-${item.item_id}`}>
                        {linkedCount > 0 ? `${item.completed_task_count} of ${linkedCount} to-dos done` : (hasFilings ? `${filings.length} filing${filings.length > 1 ? 's' : ''}` : 'No to-dos')}
                      </span>
                    </div>
                    {hasFilings ? (
                      <div className="flex items-center gap-2">
                        {filings.length === 1 ? (
                          <span style={pillStyle(filingPillColor(filings[0]))} data-testid={`scope-item-live-status-${item.item_id}`}>{filings[0].live_status || 'No status'}</span>
                        ) : (
                          <span style={pillStyle(item.is_complete ? '#16A34A' : (hasDob ? '#71717A' : '#5A7A99'))} data-testid={`scope-item-live-status-${item.item_id}`}>
                            {hasDob ? `${signedOffCount}/${dobFilings.length} signed off` : `${filings.length} permits`}
                          </span>
                        )}
                        {showManual && (
                          <Select value={item.manual_status || 'Not Started'} onValueChange={v => updateScopeItemStatus(item.item_id, v)}>
                            <SelectTrigger className="h-8 w-36 bg-background border-border text-xs" data-testid={`scope-item-manual-status-${item.item_id}`} title="Item completion (manual)">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {SCOPE_STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ) : linkedCount > 0 ? (
                      <span data-testid={`scope-item-status-pill-${item.item_id}`}><StatusDot status={item.status} /></span>
                    ) : (
                      <Select value={item.status} onValueChange={v => updateScopeItemStatus(item.item_id, v)}>
                        <SelectTrigger className="h-8 w-40 bg-background border-border text-sm" data-testid={`scope-item-status-select-${item.item_id}`}>
                          <SelectValue><StatusDot status={item.status} /></SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {SCOPE_STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-sm"><StatusDot status={s} /></SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                {open && (
                  <div className="border-t border-border p-2 space-y-1.5">
                    {item.scope_detail && (
                      <p className="text-xs text-muted-foreground px-1 pb-1 leading-snug" data-testid={`scope-item-detail-${item.item_id}`}>{item.scope_detail}</p>
                    )}

                    {/* Filing / permit links (Sections B & C — multiple allowed, like to-dos) */}
                    <div className="rounded-md border border-border bg-secondary/20 p-2" data-testid={`scope-item-filing-block-${item.item_id}`}>
                      {hasFilings && (
                        <div className="space-y-1.5 mb-1.5">
                          {filings.map((f, idx) => (
                            <div key={f.record_id || idx} className="flex items-start justify-between gap-2 flex-wrap" data-testid={`scope-item-filing-row-${item.item_id}-${idx}`}>
                              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                <Link2 className="h-3.5 w-3.5 shrink-0" style={{ color: '#1B4A76' }} />
                                <span className="text-xs" style={{ color: '#010A21' }}>
                                  {FILING_TYPE_LABELS[f.type]} {f.type === 'dob' ? 'filing' : 'permit'}
                                  <span className="ml-1" style={{ fontFamily: "'DM Mono', monospace" }}>{f.number}</span>
                                </span>
                                <span style={pillStyle(filingPillColor(f))} data-testid={`scope-item-filing-status-${item.item_id}-${idx}`}>{f.live_status || 'No status'}</span>
                                {isNoOpenDataMatch(f) && (
                                  <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs" style={{ backgroundColor: '#D977061F', color: '#B45309', fontWeight: 500 }} data-testid={`scope-item-nomatch-${item.item_id}-${idx}`}>
                                    <AlertTriangle className="h-3 w-3" /> No Open Data match, verify number
                                  </span>
                                )}
                                {f.type === 'fdny' && (
                                  <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs" style={{ backgroundColor: '#5A7A991F', color: '#5A7A99', fontWeight: 500 }} data-testid={`scope-item-unverified-${item.item_id}-${idx}`}>
                                    <Info className="h-3 w-3" /> Manually entered, not verified against city data
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {f.type !== 'fdny' && (
                                  <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 px-1.5" onClick={() => refreshFiling(item, f.record_id)} data-testid={`scope-item-refresh-filing-${item.item_id}-${idx}`}><RefreshCw className="h-3 w-3" /> Refresh</Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 px-1.5 text-destructive hover:text-destructive" onClick={() => unlinkFiling(item, f.record_id)} data-testid={`scope-item-unlink-filing-${item.item_id}-${idx}`}><X className="h-3 w-3" /> Unlink</Button>
                              </div>
                            </div>
                          ))}
                          <p className="text-xs text-muted-foreground">{hasDob ? 'Completes when all linked DOB filings are signed off.' : 'Live status only. Does not auto-complete.'}</p>
                        </div>
                      )}
                      {linkFormFor === item.item_id ? (
                        <div className="flex items-end gap-2 flex-wrap" data-testid={`scope-item-link-form-${item.item_id}`}>
                          <div>
                            <Label className="text-xs text-muted-foreground">Type</Label>
                            <Select value={linkForm.type} onValueChange={v => setLinkForm({ ...linkForm, type: v })}>
                              <SelectTrigger className="h-8 w-40 bg-background border-border text-xs mt-1" data-testid={`scope-item-link-type-${item.item_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                <SelectItem value="dob" className="text-xs">DOB Filing</SelectItem>
                                <SelectItem value="dot" className="text-xs">DOT Permit</SelectItem>
                                <SelectItem value="fdny" className="text-xs">FDNY Permit</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1 min-w-[180px]">
                            <Label className="text-xs text-muted-foreground">{linkForm.type === 'dob' ? 'Job / Filing number' : 'Permit number'}</Label>
                            <Input value={linkForm.number} onChange={e => setLinkForm({ ...linkForm, number: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') submitLinkFiling(item); }} placeholder={linkForm.type === 'dob' ? 'e.g. 123514036' : 'e.g. M012025198A73'} className="h-8 bg-background border-border text-sm mt-1" data-testid={`scope-item-link-number-${item.item_id}`} />
                          </div>
                          <Button size="sm" onClick={() => submitLinkFiling(item)} disabled={linkBusy} className="bg-primary text-primary-foreground" data-testid={`scope-item-link-submit-${item.item_id}`}>{linkBusy ? 'Linking...' : 'Link'}</Button>
                          <Button size="sm" variant="outline" onClick={() => { setLinkFormFor(null); setLinkForm({ type: 'dob', number: '' }); }}>Cancel</Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">{hasFilings ? '\u00A0' : 'No filing or permit linked to this item.'}</span>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-border gap-1" onClick={() => openLinkForm(item.item_id)} data-testid={`scope-item-link-btn-${item.item_id}`}><Link2 className="h-3 w-3" /> {hasFilings ? 'Link another filing / permit' : 'Link Filing / Permit'}</Button>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openCreateTodo(item.item_id)} data-testid={`scope-item-add-todo-${item.item_id}`}><Plus className="h-3 w-3 mr-1" /> Add To-Do</Button>
                    </div>
                    {itemTasks.map(t => renderTodoCard(t))}
                    {itemTasks.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No to-dos linked to this item</p>}
                    {todoFormOpenFor === item.item_id && createFormJsx()}
                  </div>
                )}
              </div>
            );
          })}

          {/* Project To-Dos */}
          <div className="border border-border rounded-md bg-card" data-testid="scope-other-todos">
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-medium" style={{ color: '#010A21' }}>Project To-Dos</span>
                <div className="flex items-center gap-2" data-testid="project-todos-progress">
                  <div className="w-24 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${otherTodos.length ? Math.round(otherDone / otherTodos.length * 100) : 0}%`, backgroundColor: otherTodos.length && otherDone === otherTodos.length ? '#16A34A' : '#1B4A76' }} />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="project-todos-progress-text">{otherDone} of {otherTodos.length} complete</span>
                </div>
              </div>
              <Button size="sm" variant="outline" className="border-border shrink-0" onClick={() => openCreateTodo(null)} data-testid="other-add-todo-btn"><Plus className="h-3 w-3 mr-1" /> Add To-Do</Button>
            </div>
            <div className="border-t border-border p-2 space-y-1.5">
              {otherTodos.map(t => renderTodoCard(t))}
              {otherTodos.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No unlinked to-dos</p>}
              {todoFormOpenFor === '__other' && createFormJsx()}
            </div>
          </div>
        </TabsContent>

        {/* Meeting Notes */}
        <TabsContent value="meeting-notes" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{meetingNotes.length} meeting note(s)</p>
            <Button size="sm" onClick={() => { setMeetingNoteFormOpen(!meetingNoteFormOpen); setEditMeetingNote(null); setMeetingNoteForm({ date: new Date().toISOString().slice(0, 10), attendees: '', notes: '' }); }} className="bg-primary text-primary-foreground" data-testid="add-meeting-note-btn"><Plus className="h-3 w-3 mr-1" /> Add Note</Button>
          </div>

          {meetingNoteFormOpen && (
            <Card className="border-border border-dashed bg-transparent shadow-none">
              <CardContent className="p-3 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">New Meeting Note</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" value={meetingNoteForm.date} onChange={e => setMeetingNoteForm({ ...meetingNoteForm, date: e.target.value })} className="bg-background border-border" data-testid="meeting-note-date" /></div>
                  <div><Label className="text-xs text-muted-foreground">Attendees</Label><Input placeholder="e.g. John, Sarah, Mike" value={meetingNoteForm.attendees} onChange={e => setMeetingNoteForm({ ...meetingNoteForm, attendees: e.target.value })} className="bg-background border-border" data-testid="meeting-note-attendees" /></div>
                </div>
                <div><Label className="text-xs text-muted-foreground">Notes *</Label><ExpandableTextarea placeholder="Meeting notes..." value={meetingNoteForm.notes} onChange={e => setMeetingNoteForm({ ...meetingNoteForm, notes: e.target.value })} className="bg-background border-border" rows={4} expandedRows={10} data-testid="meeting-note-content" /></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={createMeetingNote} className="bg-primary text-primary-foreground" data-testid="save-meeting-note-btn"><Plus className="h-3 w-3 mr-1" /> Save Note</Button>
                  <Button size="sm" variant="outline" onClick={() => setMeetingNoteFormOpen(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {editMeetingNote && (
            <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none">
              <CardContent className="p-3 space-y-2">
                <p className="text-xs font-semibold text-primary">Edit Meeting Note</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" value={editMeetingNoteForm.date} onChange={e => setEditMeetingNoteForm({ ...editMeetingNoteForm, date: e.target.value })} className="bg-background border-border" data-testid="edit-meeting-note-date" /></div>
                  <div><Label className="text-xs text-muted-foreground">Attendees</Label><Input placeholder="e.g. John, Sarah, Mike" value={editMeetingNoteForm.attendees} onChange={e => setEditMeetingNoteForm({ ...editMeetingNoteForm, attendees: e.target.value })} className="bg-background border-border" data-testid="edit-meeting-note-attendees" /></div>
                </div>
                <div><Label className="text-xs text-muted-foreground">Notes *</Label><ExpandableTextarea placeholder="Meeting notes..." value={editMeetingNoteForm.notes} onChange={e => setEditMeetingNoteForm({ ...editMeetingNoteForm, notes: e.target.value })} className="bg-background border-border" rows={4} expandedRows={10} data-testid="edit-meeting-note-content" /></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEditMeetingNote} className="bg-primary text-primary-foreground" data-testid="update-meeting-note-btn">Save Changes</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditMeetingNote(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {meetingNotes.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No meeting notes yet</p>
          ) : (
            meetingNotes.map(n => (
              <Card key={n.id} className="border-border bg-background shadow-none hover:border-primary/30 transition-colors" data-testid={`meeting-note-${n.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className="text-xs font-mono">{n.date}</Badge>
                        {n.attendees && <span className="text-xs text-muted-foreground">Attendees: {n.attendees}</span>}
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                      <p className="text-xs text-muted-foreground mt-1.5">{n.created_by_name && `${n.created_by_name} · `}{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => openEditMeetingNote(n)} data-testid={`edit-meeting-note-${n.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => deleteMeetingNote(n.id)} data-testid={`delete-meeting-note-${n.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="docs" className="mt-4">
          <DocumentsSection recordType="Project" recordId={selected.id} contextPropertyId={selected.property_id} />
        </TabsContent>

        {/* CO/TCO */}
        <TabsContent value="cotco" className="mt-4"><CotcoChecklist projectId={selected.id} /></TabsContent>

        <TabsContent value="dot-permits" className="mt-4"><DotPermitsTab lockedPropertyId={selected.property_id} lockedProjectId={selected.id} /></TabsContent>

        <TabsContent value="dot-batch" className="mt-4"><DotBatchTrackers lockedPropertyId={selected.property_id} lockedProjectId={selected.id} /></TabsContent>

        {/* DOB Filings */}
        <TabsContent value="dob-filings" className="mt-4"><DobFilings projectId={selected.id} propertyId={selected.property_id} /></TabsContent>

        <TabsContent value="fdny-permits" className="mt-4"><FdnyPermits lockedPropertyId={selected.property_id} lockedProjectId={selected.id} /></TabsContent>

        <TabsContent value="violations" className="mt-4"><ViolationsTab lockedPropertyId={selected.property_id} lockedProjectId={selected.id} /></TabsContent>

        {/* Inspections */}
        <TabsContent value="inspections" className="mt-4"><InspectionsTab projectId={selected.id} onUpdated={() => { refreshSelected(); loadScope(); }} /></TabsContent>
      </Tabs>

      {/* Add Status Update modal */}
      <Dialog open={addStatusOpen} onOpenChange={setAddStatusOpen}>
        <DialogContent className="bg-card border-border" aria-describedby={undefined} data-testid="add-status-modal">
          <DialogHeader>
            <DialogTitle style={{ color: '#010A21' }}>Add Status Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Status update *</Label>
            <ExpandableTextarea placeholder="What's the latest on this project?" value={statusText} onChange={e => setStatusText(e.target.value)} className="bg-background border-border" rows={3} expandedRows={7} data-testid="status-text-input" />
            <Label className="text-xs text-muted-foreground">Action Item (optional)</Label>
            <Input placeholder="A follow-up action, if any" value={actionItems} onChange={e => setActionItems(e.target.value)} className="bg-background border-border" data-testid="status-action-item-input" />
            <p className="text-xs text-muted-foreground">Adding an action item creates a reminder you can turn into a Project To-Do.</p>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={addStatusEntry} className="bg-primary text-primary-foreground" data-testid="add-status-btn"><Plus className="h-3 w-3 mr-1" /> Save Update</Button>
              <Button size="sm" variant="outline" onClick={() => setAddStatusOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email compose modal */}
      <Dialog open={emailComposeOpen} onOpenChange={setEmailComposeOpen}>
        <DialogContent className="bg-card border-border max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="send-email-modal">
          <DialogHeader>
            <DialogTitle style={{ color: '#010A21' }}>Send Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* To recipients */}
            <div data-testid="email-to-field">
              <Label className="text-xs text-muted-foreground">To *</Label>
              <div className="flex flex-wrap gap-1.5 my-1.5 min-h-[22px]" data-testid="email-to-chips">
                {toRecipients.length === 0 && <span className="text-xs text-muted-foreground">No recipients selected</span>}
                {toRecipients.map(r => (
                  <span key={r.email} className="inline-flex items-center gap-1 rounded-full border border-[#1B4A76]/40 bg-[#1B4A76]/10 px-2 py-0.5 text-[11px]" style={{ color: '#1B4A76' }} data-testid={`email-to-chip-${r.email}`}>
                    <span className="font-medium">{r.label !== 'Custom address' ? r.label : 'Custom'}</span>
                    <span className="opacity-70">· {r.email}</span>
                    <button type="button" onClick={() => removeRecipient(toRecipients, setToRecipients, r.email)} className="hover:text-scarlet" data-testid={`email-to-remove-${r.email}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              {recipientOptions.length > 0 && (
                <Select value="" onValueChange={(key) => { const o = recipientOptions.find(x => x.key === key); if (o) addRecipient(toRecipients, setToRecipients, o.email, optLabel(o)); }}>
                  <SelectTrigger className="bg-background border-border" data-testid="email-to-select"><SelectValue placeholder="Add from contacts" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {recipientOptions.filter(o => !toRecipients.some(r => r.email === o.email)).map(o => (
                      <SelectItem key={o.key} value={o.key} data-testid={`email-to-option-${o.key}`}>
                        <span className="flex flex-col text-left">
                          <span className="text-sm">{optLabel(o)}</span>
                          <span className="text-xs text-muted-foreground">{o.email}</span>
                        </span>
                      </SelectItem>
                    ))}
                    {recipientOptions.filter(o => !toRecipients.some(r => r.email === o.email)).length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">All contacts added</div>
                    )}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 mt-1.5">
                <Input type="email" value={toCustom} onChange={e => setToCustom(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(toRecipients, setToRecipients, toCustom, 'Custom address'); setToCustom(''); } }} placeholder="Add a custom address" className="bg-background border-border" data-testid="email-to-input" />
                <Button type="button" variant="outline" size="sm" className="border-border shrink-0" onClick={() => { addRecipient(toRecipients, setToRecipients, toCustom, 'Custom address'); setToCustom(''); }} data-testid="email-to-add-btn">Add</Button>
              </div>
            </div>

            {/* CC recipients */}
            <div data-testid="email-cc-field">
              <Label className="text-xs text-muted-foreground">CC</Label>
              <div className="flex flex-wrap gap-1.5 my-1.5 min-h-[22px]" data-testid="email-cc-chips">
                {ccRecipients.length === 0 && <span className="text-xs text-muted-foreground">No CC recipients</span>}
                {ccRecipients.map(r => (
                  <span key={r.email} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px]" style={{ color: '#334155' }} data-testid={`email-cc-chip-${r.email}`}>
                    <span className="font-medium">{r.label !== 'Custom address' ? r.label : 'Custom'}</span>
                    <span className="opacity-70">· {r.email}</span>
                    <button type="button" onClick={() => removeRecipient(ccRecipients, setCcRecipients, r.email)} className="hover:text-scarlet" data-testid={`email-cc-remove-${r.email}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              {recipientOptions.length > 0 && (
                <Select value="" onValueChange={(key) => { const o = recipientOptions.find(x => x.key === key); if (o) addRecipient(ccRecipients, setCcRecipients, o.email, optLabel(o)); }}>
                  <SelectTrigger className="bg-background border-border" data-testid="email-cc-select"><SelectValue placeholder="Add from contacts" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {recipientOptions.filter(o => !ccRecipients.some(r => r.email === o.email)).map(o => (
                      <SelectItem key={o.key} value={o.key} data-testid={`email-cc-option-${o.key}`}>
                        <span className="flex flex-col text-left">
                          <span className="text-sm">{optLabel(o)}</span>
                          <span className="text-xs text-muted-foreground">{o.email}</span>
                        </span>
                      </SelectItem>
                    ))}
                    {recipientOptions.filter(o => !ccRecipients.some(r => r.email === o.email)).length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">All contacts added</div>
                    )}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 mt-1.5">
                <Input type="email" value={ccCustom} onChange={e => setCcCustom(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(ccRecipients, setCcRecipients, ccCustom, 'Custom address'); setCcCustom(''); } }} placeholder="Add a custom address" className="bg-background border-border" data-testid="email-cc-input" />
                <Button type="button" variant="outline" size="sm" className="border-border shrink-0" onClick={() => { addRecipient(ccRecipients, setCcRecipients, ccCustom, 'Custom address'); setCcCustom(''); }} data-testid="email-cc-add-btn">Add</Button>
              </div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Subject *</Label><Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Subject line" className="bg-background border-border" data-testid="email-subject-input" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Message *</Label>
              <RichTextEditor
                value={emailForm.body}
                onChange={(html) => setEmailForm({ ...emailForm, body: html })}
                placeholder="Write your message..."
                minHeight={150}
                testid="email-body-editor"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Your saved signature is added automatically to the bottom of this email. Set or edit it in My Profile.</p>
              <div className="mt-2">
                <Label className="text-[11px] text-muted-foreground">Signature preview (added automatically, not editable here)</Label>
                <div
                  className="mt-1 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 opacity-70 select-none pointer-events-none"
                  data-testid="email-signature-preview"
                >
                  {user?.signature && user.signature.trim() ? (
                    <div className="email-html-body text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: sanitizeHtml(user.signature) }} />
                  ) : (
                    <span className="text-[11px] text-muted-foreground">No signature set. Add one in My Profile to include it automatically.</span>
                  )}
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attach files</Label>
                <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()} className="text-xs text-[#1B4A76] hover:underline flex items-center gap-1" data-testid="email-upload-new-btn">
                  <Upload className="h-3 w-3" /> Upload new file
                </button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickNewFiles} data-testid="email-file-input" />
              </div>

              {projectDocs.length > 0 && (
                <div className="rounded-md border border-border bg-background max-h-32 overflow-y-auto p-1" data-testid="email-doc-picker">
                  {projectDocs.map(doc => (
                    <label key={doc.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer" data-testid={`email-doc-option-${doc.id}`}>
                      <Checkbox checked={selectedDocIds.includes(doc.id)} onCheckedChange={() => toggleDocSelected(doc.id)} data-testid={`email-doc-checkbox-${doc.id}`} />
                      <File className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs truncate" style={{ color: '#334155' }}>{doc.name}</span>
                      {doc.category && <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{doc.category}</span>}
                    </label>
                  ))}
                </div>
              )}
              {projectDocs.length === 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="email-no-docs">No documents linked to this project yet. Upload a new file to attach one.</p>
              )}

              {(selectedDocIds.length > 0 || newFiles.length > 0) && (
                <div className="flex flex-wrap gap-1.5" data-testid="email-attachment-chips">
                  {selectedDocIds.map(did => {
                    const d = projectDocs.find(x => x.id === did);
                    return (
                      <span key={did} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px]" style={{ color: '#334155' }} data-testid={`email-chip-doc-${did}`}>
                        <File className="h-3 w-3" /> {d ? d.name : did}
                        <button type="button" onClick={() => toggleDocSelected(did)} className="hover:text-scarlet" data-testid={`email-chip-remove-doc-${did}`}><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                  {newFiles.map((f, idx) => (
                    <span key={`${f.name}-${idx}`} className="inline-flex items-center gap-1 rounded-full border border-[#1B4A76]/40 bg-[#1B4A76]/10 px-2 py-0.5 text-[11px]" style={{ color: '#1B4A76' }} data-testid={`email-chip-newfile-${idx}`}>
                      <Upload className="h-3 w-3" /> {f.name} <span className="text-[10px] opacity-70">(new)</span>
                      <button type="button" onClick={() => setNewFiles(prev => prev.filter((_, i) => i !== idx))} className="hover:text-scarlet" data-testid={`email-chip-remove-newfile-${idx}`}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={sendProjectEmail} disabled={sendingEmail} className="bg-primary text-primary-foreground" data-testid="send-email-submit"><Send className="h-3.5 w-3.5 mr-1" /> {sendingEmail ? 'Sending...' : 'Send Email'}</Button>
              <Button variant="outline" onClick={() => setEmailComposeOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email History modal */}
      <Dialog open={emailHistoryOpen} onOpenChange={setEmailHistoryOpen}>
        <DialogContent className="bg-card border-border" aria-describedby={undefined} data-testid="email-history-modal">
          <DialogHeader>
            <DialogTitle style={{ color: '#010A21' }}>Email History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {projectEmails.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6" data-testid="email-history-empty">No emails sent yet</p>
            ) : (
              projectEmails.map(em => (
                <Card key={em.id} className="border-border bg-background shadow-none" data-testid={`email-history-entry-${em.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-sm font-medium" style={{ color: '#010A21' }}>{em.subject}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: em.delivered ? '#DCFCE7' : '#FEF3C7', color: em.delivered ? '#166534' : '#92400E' }} data-testid={`email-status-${em.id}`}>{em.delivered ? 'Delivered' : 'Logged'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">To: {em.to}</p>
                    {em.cc && <p className="text-xs text-muted-foreground" data-testid={`email-cc-${em.id}`}>Cc: {em.cc}</p>}
                    {em.body_html ? (
                      <div className="email-html-body mt-1" data-testid={`email-body-${em.id}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(em.body_html) }} />
                    ) : (
                      <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: '#334155' }} data-testid={`email-body-${em.id}`}>{em.body}</p>
                    )}
                    {Array.isArray(em.attachment_filenames) && em.attachment_filenames.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`email-attachments-${em.id}`}>
                        {em.attachment_filenames.map((fn, i) => (
                          <span key={`${fn}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px]" style={{ color: '#334155' }} data-testid={`email-attachment-${em.id}-${i}`}>
                            <Paperclip className="h-3 w-3" /> {fn}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-1.5">{em.sent_by_name ? `${em.sent_by_name} · ` : ''}{new Date(em.sent_at).toLocaleString()}{em.delivered ? '' : ` · ${em.status || ''}`}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Log history modal */}
      <Dialog open={statusLogOpen} onOpenChange={setStatusLogOpen}>
        <DialogContent className="bg-card border-border" aria-describedby={undefined} data-testid="status-log-modal">
          <DialogHeader>
            <DialogTitle style={{ color: '#010A21' }}>Status Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(selected.status_log || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6" data-testid="status-log-empty">No status updates yet</p>
            ) : (
              (selected.status_log || []).map(s => (
                <Card key={s.id} className="border-border bg-background shadow-none" data-testid={`status-log-entry-${s.id}`}>
                  <CardContent className="p-3">
                    <p className="text-sm" style={{ color: '#010A21' }}>{s.status_text}</p>
                    {s.action_items && <p className="text-xs text-muted-foreground mt-1">Action: {s.action_items}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(s.created_at).toLocaleString()}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto" data-testid="project-edit-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Edit Project</SheetTitle>
            <SheetDescription>Update project details. Changes save immediately.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Project Name *</Label><Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-background border-border" data-testid="project-edit-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Client</Label>
              <div className="mt-1"><SearchableSelect options={clientOptions} value={editForm.client_id} onChange={v => setEditForm({ ...editForm, client_id: v })} placeholder="Select a client" searchPlaceholder="Search clients..." emptyText="No clients." testid="project-edit-client" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Property</Label>
              <div className="mt-1"><SearchableSelect options={propertyOptions} value={editForm.property_id} onChange={v => setEditForm({ ...editForm, property_id: v })} placeholder="Link a property" searchPlaceholder="Search properties..." emptyText="No properties." testid="project-edit-property" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Project Manager</Label>
              <Select value={editForm.project_manager_id || '__none'} onValueChange={v => setEditForm({ ...editForm, project_manager_id: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="project-edit-pm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {users.filter(u => u.role !== 'Client' && u.role !== 'Administrator').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Primary Agency</Label><Input value={editForm.primary_agency} onChange={e => setEditForm({ ...editForm, primary_agency: e.target.value })} className="bg-background border-border" data-testid="project-edit-agency" /></div>
              <div><Label className="text-xs text-muted-foreground">Contract Type</Label><Input value={editForm.contract_type} onChange={e => setEditForm({ ...editForm, contract_type: e.target.value })} className="bg-background border-border" data-testid="project-edit-contract-type" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Start Date</Label><Input type="date" value={editForm.start_date || ''} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} className="bg-background border-border" data-testid="project-edit-start-date" /></div>
              <div><Label className="text-xs text-muted-foreground">End Date</Label><Input type="date" value={editForm.end_date || ''} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} className="bg-background border-border" data-testid="project-edit-end-date" /></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Quote No.</Label><Input value={editForm.quote_number} onChange={e => setEditForm({ ...editForm, quote_number: e.target.value })} className="bg-background border-border" data-testid="project-edit-quote" /></div>
            <div><Label className="text-xs text-muted-foreground">Address</Label><Input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} className="bg-background border-border" data-testid="project-edit-address" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label><ExpandableTextarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="bg-background border-border" rows={3} expandedRows={8} data-testid="project-edit-description" /></div>
            <div className="flex items-center gap-2 pt-1">
              <Button onClick={saveEdit} disabled={savingEdit} className="bg-primary text-primary-foreground" data-testid="project-edit-save">{savingEdit ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button variant="outline" className="text-destructive hover:text-destructive border-border ml-auto" onClick={deleteProject} data-testid="project-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
